import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from '@apollo/react-hooks';

import FileCopyIcon from '@material-ui/icons/FileCopy';

import { Pagination } from 'components';
import ConfirmContext from 'contexts/ConfirmContext';

import { ACCOMMODATIONS } from 'graphql/queries/accommodations';
import { DELETE_ACCOMMODATION } from 'graphql/mutations/accommodation';

import { TrashIcon, EditIcon } from 'components/icons';

import ScheduleDates from 'pages/accommodations-and-activities/components/schedule-dates/ScheduleDates';

import {
  Typography,
  IconButton,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table
} from '@material-ui/core';

import useStyles from './styles';

const headingsConfig = classes => {
  return [
    { label: 'Photo', cellClassName: classes.imgCell, align: 'left' },
    {
      label: 'Accommodation name',
      cellClassName: classes.nameCell,
      align: 'left'
    },
    {
      label: 'Status',
      cellClassName: classes.availabilityCell,
      align: 'left'
    },
    {
      label: 'Availability',
      cellClassName: classes.scheduleCell,
      align: 'left'
    },
    {
      label: 'Product code',
      cellClassName: classes.productCodeCell,
      align: 'left'
    },
    { label: 'Guests', cellClassName: classes.guestsCell, align: 'left' },
    { label: 'Actions', align: 'right' }
  ];
};

function AccommodationsList({ deleteCallback, ownerId }) {
  const classes = useStyles();
  const [t] = useTranslation();
  const history = useHistory();
  const confirm = React.useContext(ConfirmContext);
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = React.useState(0);

  const { data, refetch } = useQuery(ACCOMMODATIONS, {
    variables: {
      businessOwnerId: ownerId,
      page: page
    }
  });
  const [deleteAccommodation] = useMutation(DELETE_ACCOMMODATION);

  React.useEffect(() => {
    refetch();
  }, []);

  const onDeleteHandler = item => {
    confirm({
      title: 'Delete Accommodation?',
      description: `${item.title} • ${item.productCode}`,
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() =>
        deleteAccommodation({ variables: { id: item.id } }).then(() => {
          deleteCallback();
          enqueueSnackbar(t('Accommodation was successfully removed!'), {
            variant: 'success'
          });
          if (data.accommodations.length <= 1 && page) {
            refetch({
              page: page - 1
            });
            setPage(page - 1);
          } else {
            refetch({ page });
          }
        })
      )
      .catch(() => {});
  };

  const onPageChangeHandler = (e, page) => {
    refetch({
      page: page - 1
    });
    setPage(page - 1);
  };

  return (
    <TableContainer component="div" classes={{ root: classes.container }}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead className={classes.tableHead}>
          <TableRow>
            {headingsConfig(classes).map(heading => (
              <TableCell
                key={heading.label}
                align={heading.align}
                className={classNames(
                  classes.tableCell,
                  classes.headTableCell,
                  heading.cellClassName
                )}
              >
                <Typography variant="h3" className={classes.heading}>
                  {t(heading.label)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data &&
            data.accommodations &&
            data.accommodations.map(accommodation => (
              <TableRow
                key={accommodation.id}
                classes={{ root: classes.tableRow }}
                onClick={() =>
                  history.push(`/accommodations/${accommodation.id}`)
                }
              >
                <TableCell
                  className={classNames(classes.tableCell, classes.imgCell)}
                  component="th"
                  scope="row"
                >
                  <div className={classes.imageWrapper}>
                    {accommodation.photo && (
                      <img
                        src={accommodation.photo}
                        alt=""
                        className={classes.image}
                      />
                    )}
                  </div>
                </TableCell>

                <TableCell
                  className={classNames(classes.tableCell, classes.nameCell)}
                  align="left"
                >
                  {accommodation.title}
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCell,
                    classes.availabilityCell
                  )}
                  align="left"
                >
                  {accommodation.availability ? (
                    <Typography color="primary">{t('Active')}</Typography>
                  ) : (
                    <Typography color="inherit">{t('Not active')}</Typography>
                  )}
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCell,
                    classes.scheduleCell
                  )}
                  align="left"
                >
                  <ScheduleDates item={accommodation} />
                </TableCell>
                <TableCell
                  className={classNames(
                    classes.tableCell,
                    classes.productCodeCell
                  )}
                  align="left"
                >
                  {accommodation.productCode}
                </TableCell>
                <TableCell
                  className={classNames(classes.tableCell, classes.guestsCell)}
                  align="left"
                >
                  {accommodation.guestsNumber}
                </TableCell>
                <TableCell className={classes.tableCell} align="right">
                  <div style={{ display: 'inline-flex', paddingRight: '10px' }}>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        history.push(
                          `/accommodations/${accommodation.id}/copy`
                        );
                      }}
                      className={classes.iconButton}
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      aria-label="edit"
                    >
                      <FileCopyIcon
                        style={{
                          color: '#59758B',
                          width: '18px',
                          height: '18px'
                        }}
                      />
                    </IconButton>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        history.push(`/accommodations/${accommodation.id}`);
                      }}
                      className={classes.iconButton}
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      aria-label="edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteHandler(accommodation);
                      }}
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      className={classes.iconButton}
                      aria-label="delete"
                    >
                      <TrashIcon />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {data && (
        <Pagination
          page={page + 1}
          count={data.numberOfAccommodationsPages}
          onChange={onPageChangeHandler}
          amount={data.accommodations.length}
          total={data.accommodationsActivitiesOverview.accommodations.total}
        />
      )}
    </TableContainer>
  );
}

export default AccommodationsList;
